'use client';

import React from 'react';
import { useTrackingCode } from 'react-hubspot-tracking-code-hook';

import HeroValdProd from '@src/components/Sections/HeroValdProd';
import RenderBodyCopy from '@src/components/Shared/RenderBodyCopy';
import RenderButtons from '@src/components/Shared/RenderButtons';
import Container from '@src/components/Shared/Container';
import SectionHeader from '@src/components/Shared/SectionHeader';
import NextImage from '@src/components/Shared/NextImage';
import InlineSVG from '@src/components/Shared/InlineSVG';
import VideoEmbedPlayer from '@src/components/Shared/VideoEmbedPlayer/VideoEmbedPlayer';

import ImageTextTertiary from '@src/components/Sections/ImageTextTertiary';
import ImageTextSecondary from '@src/components/Sections/ImageTextSecondary';
import VideoTextPrimary from '@src/components/Sections/VideoTextPrimary';
import FeaturedProducts from '@src/components/Sections/FeaturedProducts';
import CallToAction from '@src/components/Sections/CallToAction';

import usePreviewContent from '@src/hooks/usePreviewContent';

import constructURL from '@src/utils/constructURL';

import { NordBordPageClientPropTypes } from './NordBordPage.types';
import SubscriptionTable from '@src/components/Shared/SubscriptionTable';

const NordBordPageClient: React.FC<NordBordPageClientPropTypes> = ({ data }) => {
  // Enable live updates for the post
  const page = usePreviewContent({ content: data?.page });

  // Set the page view path for analytics tracking
  const { setPathPageView, setContentType } = useTrackingCode();
  setPathPageView(
    constructURL({
      languageCode: page?.languageCode?.toLowerCase(),
      slug: `/products/nordbord`,
      baseURL: '',
    })
  );
  setContentType('site-page');

  return (
    <>
      <HeroValdProd
        id={page?.hero?.items?.[0]?.scrollTo?.slice(1)}
        ctfData={page?.hero?.items?.[0]}
        className=''
        imgClassName='relative overflow-hidden mt-[2rem] md:absolute md:right-[0] md:w-[45rem] md:bottom-[2rem] lg:mt-[0rem] lg:relative lg:w-[50rem] lg:bottom-[-2rem] lg:left-[4rem] 2xl:left-[5rem] 2xl:w-[60rem] 3xl:w-[70rem] 3xl:left-[-10rem] xsm:min-h-[30rem] md:min-h-[35.8156rem] lg:min-h-[37.6109rem] 2xl:min-h-[47.4328rem] 3xl:min-h-[55.7141]'
        logoClassName='relative overflow-hidden mt-[3rem] md:mt-[5.7rem] max-w-[27.6386rem] mb-[2.4rem] md:max-w-[25.7rem] md:mt-[4.1rem] lg:max-w-[35.9302rem] 2xl:max-w-[45rem] xsm:min-h-[5.8734rem] md:min-h-[5.8734rem] lg:min-h-[8.2125rem] 2xl:min-h-[10.2844rem] xsm:min-h-[6.3156rem] md:min-h-[5.8734rem] lg:min-h-[8.2125rem] 2xl:min-h-[10.2844rem]'
        introClassName='tracking-[-0.0342rem] leading-[2.7rem] text-[1.8rem] max-w-[30.5rem] md:text-[2rem] md:leading-[3rem] md:tracking-[-0.036rem] md:max-w-[39.6rem] xl:max-w-[48.5rem] lg:text-[2.4rem] lg:leading-[3.6rem] 2xl:max-w-[58.3rem]'
        headerClassName='md:w-[80%] md:self-start lg:self-center lg:w-[50%] lg:mt-[-15rem]'
      />

      <ImageTextTertiary
        reversed
        id={page?.theGoldStandard?.items?.[0]?.scrollTo?.slice(1)}
        background='bg-background-tertiary pb-[1rem] pt-[4rem] lg:pb-[0]'
        title={page?.theGoldStandard?.items?.[0]?.title}
        titleClassName='md:max-w-[43rem] [&>span]:drop-shadow-xs'
        imgURL={page?.theGoldStandard?.items?.[0]?.mediaPrimary?.url}
        imgAlt={page?.theGoldStandard?.items?.[0]?.mediaPrimary?.description ?? 'Vald image'}
        aspectRatio={
          page?.theGoldStandard?.items?.[0]?.mediaPrimary?.width /
          page?.theGoldStandard?.items?.[0]?.mediaPrimary?.height
        }
        width={page?.theGoldStandard?.items?.[0]?.mediaPrimary?.width}
        imgSecondClassName='mt-[-8rem] 2xl:w-[70%]'
        objectFit='contain'
        childrenElements={
          <>
            <RenderBodyCopy
              className='text-left 2xl:max-w-[58.2rem]'
              bodyCopy={page?.theGoldStandard?.items?.[0]?.bodyCopy?.json?.content}
            />
            <RenderButtons items={page?.theGoldStandard?.items?.[0]?.buttonCollection?.items} />
          </>
        }
      />

      <VideoTextPrimary
        id={page?.speedAndEase?.items?.[0]?.scrollTo?.slice(1)}
        className='my-[4rem]! items-center! 3xl:pt-[5rem]!'
        videoClassName='order-0 lg:order-0 lg:w-7/12'
        textContainerClassName='lg:w-6/12 mt-[0rem]! lg:mt-[15rem]'
        titleClassName='lg:locale-fr:max-w-[55rem]'
        videoURL={page?.speedAndEase?.items?.[0]?.mediaPrimary?.url}
        pretitle={page?.speedAndEase?.items?.[0]?.preTitle}
        title={page?.speedAndEase?.items?.[0]?.title}
        childrenElements={
          <>
            <RenderBodyCopy
              className='text-left 2xl:max-w-[58.2rem]'
              bodyCopy={page?.speedAndEase?.items?.[0]?.bodyCopy?.json?.content}
            />
            <RenderButtons items={page?.speedAndEase?.items?.[0]?.buttonCollection?.items} />
          </>
        }
      />

      <ImageTextSecondary
        reversed
        id={page?.eccentricIsometric?.items?.[0]?.scrollTo?.slice(1)}
        background='bg-background-tertiary'
        className='overflow-visible! py-[4rem]'
        title={page?.eccentricIsometric?.items?.[0]?.title}
        titleClassName='[&>span]:drop-shadow-xs'
        imgURL={page?.eccentricIsometric?.items?.[0]?.mediaPrimary?.url}
        imgAlt={page?.eccentricIsometric?.items?.[0]?.mediaPrimary?.description}
        imgSecondClassName='lg:w-[70%] lg:locale-fr:w-[50%]'
        objectFit='contain'
        childrenElements={
          <>
            <RenderBodyCopy
              className='text-left lg:max-w-[50rem]'
              bodyCopy={page?.eccentricIsometric?.items?.[0]?.bodyCopy?.json?.content}
            />
            <RenderButtons items={page?.eccentricIsometric?.items?.[0]?.buttonCollection?.items} />
          </>
        }
      />

      <Container
        id={page?.nordBordVideo?.items?.[0]?.scrollTo?.slice(1)}
        className='flex flex-col gap-[4.2rem]'
      >
        <SectionHeader
          childrenClassName='max-w-[111rem]! xl:max-w-[111rem]! w-full'
          pretitle={page?.nordBordVideo?.items?.[0]?.preTitle}
          title={page?.nordBordVideo?.items?.[0]?.title}
          titleClassName={`text-[2.8rem] mb-[1.8rem] lg:mb-[3.2rem] lg:text-[4.8rem] lg:leading-[5.2rem] lg:tracking-[-0.096rem]`}
          align='center'
        >
          <>
            <RenderBodyCopy
              className='mx-auto lg:max-w-[78rem] lg:text-center'
              bodyCopy={page?.nordBordVideo?.items?.[0]?.bodyCopy?.json?.content}
            />
          </>
        </SectionHeader>

        <VideoEmbedPlayer
          className='my-[0] overflow-hidden rounded-3xl shadow-[0_4px_15px_0_rgba(0,0,0,0.25)] lg:mx-auto lg:w-[86rem]'
          data={{
            platform: 'Vimeo',
            embedUrl: `${page?.nordBordVideo?.items?.[0]?.extraAssetsCollection?.items?.[0]?.embedUrl}&background=1&loop=1&autoplay=1&badge=0&autopause=0&player_id=0&app_id=58479`,
          }}
        />
      </Container>

      <Container
        id={page?.trainingModeVideo?.items?.[0]?.scrollTo?.slice(1)}
        className={`flex flex-col gap-[4.2rem] md:gap-[3.2rem] lg:flex-row lg:items-center lg:gap-[6.4rem]`}
      >
        <SectionHeader
          className={`lg:relative lg:z-5 lg:w-[50%] lg:py-[10rem]`}
          pretitle={page?.trainingModeVideo?.items?.[0]?.preTitle}
          title={page?.trainingModeVideo?.items?.[0]?.title}
          preTitleClassName='mb-[1.6rem]'
          titleClassName={`text-[2.8rem] mb-[1.8rem] lg:mb-[3.2rem] lg:text-[4.8rem] lg:leading-[5.2rem] lg:tracking-[-0.096rem]`}
          childrenClassName='relative'
          align='left'
        >
          <RenderBodyCopy
            className='max-w-[45rem]'
            bodyCopy={page?.trainingModeVideo?.items?.[0]?.bodyCopy?.json?.content}
          />
        </SectionHeader>

        {/* TODO Swap to VideoEmbedPlayer component once training mode vide is update */}
        <div className='relative aspect-video w-full overflow-hidden lg:relative lg:z-20 lg:order-1 lg:h-[50rem]'>
          <iframe
            className='inset-0 absolute block h-full w-full border-0'
            src={`${page?.trainingModeVideo?.items?.[0]?.extraAssetsCollection?.items?.[0]?.embedUrl}&background=1&loop=1&autoplay=1&badge=0&autopause=0&player_id=0&app_id=58479`}
            allowFullScreen
            allow='autoplay; fullscreen; encrypted-media; picture-in-picture; loop;'
            title='Video player'
          />
        </div>
      </Container>

      <ImageTextSecondary
        id={page?.usability?.items?.[0]?.scrollTo?.slice(1)}
        background='lg:ml-[unset]! lg:mr-[unset]! lg:w-full!'
        pretitle={page?.usability?.items?.[0]?.preTitle}
        title={page?.usability?.items?.[0]?.title}
        imgURL={page?.usability?.items?.[0]?.mediaPrimary?.url}
        imgAlt={page?.usability?.items?.[0]?.mediaPrimary?.description}
        objectFit='contain'
        childrenElements={
          <>
            <RenderBodyCopy
              className='text-left 2xl:max-w-[58.2rem] [&>p>a]:drop-shadow-xs'
              bodyCopy={page?.usability?.items?.[0]?.bodyCopy?.json?.content}
            />
            <RenderButtons items={page?.usability?.items?.[0]?.buttonCollection?.items} />
          </>
        }
      />

      <Container
        id={page?.oneOfAKind?.items?.[0]?.scrollTo?.slice(1)}
        background='bg-background-tertiary'
        className='mb-[0rem]! bg-background-tertiary py-[7.4rem] md:flex md:flex-col lg:mb-[10.5rem]'
      >
        <SectionHeader
          className='justify-center md:mb-[3.4rem] lg:pb-[unset]'
          pretitle={page?.oneOfAKind?.items?.[0]?.preTitle}
          title={page?.oneOfAKind?.items?.[0]?.title}
          titleClassName='max-w-[71.6rem] [&>span]:drop-shadow-xs text-[2.8rem] xl:text-[4.8rem] mb-[2.4rem] leading-[3.2rem] md:leading-[4.6rem] xl:leading-[5rem] font-light'
          align='center'
        >
          <>
            <RenderBodyCopy
              className='  2xl:max-w-[78.2rem]'
              bodyCopy={page?.oneOfAKind?.items?.[0]?.bodyCopy?.json?.content}
            />
            <RenderButtons
              className='justify-center'
              items={page?.oneOfAKind?.items?.[0]?.buttonCollection?.items}
            />
          </>
        </SectionHeader>
        <NextImage
          className='ml-[-15%] mt-[4rem] hidden w-[120%] md:block 2xl:w-[110%]'
          src={page?.oneOfAKind?.items?.[0]?.mediaPrimary?.url}
          alt={page?.oneOfAKind?.items?.[0]?.mediaPrimary?.description ?? 'VALD Image'}
          aspectRatio={
            page?.oneOfAKind?.items?.[0]?.mediaPrimary?.width /
            page?.oneOfAKind?.items?.[0]?.mediaPrimary?.height
          }
          width={page?.oneOfAKind?.items?.[0]?.mediaPrimary?.width} // Set actual width
        />
        <InlineSVG
          className='mt-[4rem] w-full md:hidden'
          src={page?.oneOfAKind?.items?.[0]?.mediaSecondary?.url}
          renderAt={['mobile']}
        />
      </Container>

      <Container
        id={page?.valdSubscription?.items?.[0]?.scrollTo?.slice(1)}
        background='bg-[#F9F9F9]'
        className='mt-[0]! pb-[8rem] pt-[0]'
      >
        <SectionHeader
          className='mb-[2.4rem] lg:mb-[unset]'
          childrenClassName='w-full'
          pretitle={page?.valdSubscription?.items?.[0]?.preTitle}
          title={page?.valdSubscription?.items?.[0]?.title}
          titleClassName='text-[2.8rem] xl:text-[4.8rem] [&>span]:inline-block mb-[2.4rem] leading-[3.2rem] md:leading-[4.6rem] xl:leading-[5rem] font-light xl:max-w-[60rem] inline-block'
          align='center'
        >
          <RenderButtons
            items={page?.valdSubscription?.items?.[0]?.buttonCollection?.items}
            className='mt-[1rem]! justify-center'
          />
        </SectionHeader>
        <SubscriptionTable
          headerImgL='w-[13rem] top-[-0.8rem] lg:w-[19.6rem] ml-[-3rem]'
          headerImgR='w-[9.5rem] lg:w-[12.7rem]'
          data={page?.valdSubscription?.items}
        />
      </Container>

      <FeaturedProducts data={page?.valdSystems?.items?.[0]} />

      <CallToAction
        id={page?.callToAction?.items?.[0]?.scrollTo?.slice(1)}
        title={page?.callToAction?.items?.[0]?.title}
        bodyCopy={page?.callToAction?.items?.[0]?.bodyCopy?.json?.content}
        buttons={page?.callToAction?.items?.[0]?.buttonCollection?.items}
      />
    </>
  );
};

export default NordBordPageClient;
